import { PATHNAME_CHECKER } from '../../analytics/LocationTrackingUtils.js';
import { useState, useEffect } from 'react';

/**
 * Hook to detect the current page context and extract relevant information
 * like stacklineSku for PDP pages
 *
 * Note: This hook uses window.location directly to be compatible with apps
 * that don't use react-router. For apps with react-router (like landing-page),
 * you can pass pageContext as a prop instead if you need react-router integration.
 */
const usePageContext = () => {
    // Use window.location to be compatible with apps without react-router
    const [currentPathname, setCurrentPathname] = useState(typeof window !== 'undefined' ? window.location.pathname : '/');
    useEffect(() => {
        const updatePathname = () => setCurrentPathname(window.location.pathname);
        // Listen for popstate (browser back/forward)
        window.addEventListener("popstate", updatePathname);
        // Also poll for URL changes to catch React Router navigation
        // This is needed because React Router doesn't trigger popstate events
        const intervalId = setInterval(() => {
            if (window.location.pathname !== currentPathname) {
                updatePathname();
            }
        }, 100); // Check every 100ms
        // Update on mount in case URL changed
        setCurrentPathname(window.location.pathname);
        return () => {
            window.removeEventListener("popstate", updatePathname);
            clearInterval(intervalId);
        };
    }, [currentPathname]);
    // Check for PDP patterns:
    // - /pdp/{stacklineSku} (via PATHNAME_CHECKER)
    // - /p/{stacklineSku}/product (with or without product title prefix)
    // - /{product-title}/p/{stacklineSku}/product
    const pdpMatch = PATHNAME_CHECKER.isPDP(currentPathname);
    const pPattern = /\/p\/([^/]+)\/product/i; // Match /p/{sku}/product anywhere in path
    const pMatchResult = currentPathname.match(pPattern);
    if (pdpMatch || pMatchResult) {
        // Extract stacklineSku from various URL patterns
        const pdpPattern = /^\/pdp\/([^/]+)/i;
        const pdpMatchResult = currentPathname.match(pdpPattern);
        const stacklineSku = (pdpMatchResult === null || pdpMatchResult === void 0 ? void 0 : pdpMatchResult[1]) || (pMatchResult === null || pMatchResult === void 0 ? void 0 : pMatchResult[1]);
        if (stacklineSku) {
            return {
                pageType: "pdp",
                stacklineSku,
            };
        }
    }
    // Check if we're on a brand page
    if (PATHNAME_CHECKER.isBrandPage(currentPathname)) {
        return {
            pageType: "brand",
        };
    }
    // Check if we're on a brandclub page
    if (PATHNAME_CHECKER.isBrandclubPage(currentPathname)) {
        return {
            pageType: "brandclub",
        };
    }
    return {
        pageType: "other",
    };
};

export { usePageContext };
//# sourceMappingURL=usePageContext.js.map
